import type { AxiosResponse } from 'axios';
import { getErrorMessage, isNetworkError, isTimeoutError, normalizeBlobError } from '@/lib/apiClient';

/**
 * Saving a file the API produced, in one place.
 *
 * Every download endpoint had its own copy of "read Content-Disposition, make an object URL, click
 * a synthetic anchor, revoke" — the generated detail views, the list export, the print action — and
 * they disagreed on the details that matter: which ones parse the header at all, and what a file is
 * called when they cannot.
 */

/** Extension to fall back on when the server's file name is unavailable, by media type. */
const EXTENSION_BY_TYPE: Record<string, string> = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'application/pdf': 'pdf',
  'application/json': 'json',
  'application/xml': 'xml',
  'text/csv': 'csv',
  'text/plain': 'txt',
};

/**
 * The name the server chose, from Content-Disposition.
 *
 * Handles both `filename="x"` and RFC 5987 `filename*=UTF-8''x`. Returns null when the header is
 * absent — which is the normal case for a cross-origin response whose CORS policy does not expose
 * it, so the caller must always have a fallback.
 */
export function fileNameFromResponse(response: AxiosResponse<Blob>): string | null {
  const disposition = response.headers?.['content-disposition'] as string | undefined;
  if (!disposition) return null;

  const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  if (!match) return null;

  try {
    return decodeURIComponent(match[1].replace(/"$/, ''));
  } catch {
    // A name with a stray % is not a reason to fail the download.
    return match[1].replace(/"$/, '');
  }
}

/**
 * Save a blob response to the user's downloads.
 *
 * @param response  The blob response, from a request made with `responseType: 'blob'`.
 * @param fallbackStem  File name to use when the server did not supply one, without extension —
 *                      the extension is taken from the blob's media type so the file still opens.
 */
export function saveBlobResponse(response: AxiosResponse<Blob>, fallbackStem: string): void {
  const extension = EXTENSION_BY_TYPE[response.data?.type ?? ''];
  const fallback = extension ? `${fallbackStem}.${extension}` : fallbackStem;
  const fileName = fileNameFromResponse(response) ?? fallback;

  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * What to tell the user when a download fails.
 *
 * `responseType: 'blob'` wraps the API's error body in a Blob, so the real message has to be
 * unwrapped before it can be read; without that the user sees the generic status text.
 * getErrorMessage's network and timeout wording is written for saving a form ("your changes are
 * preserved"), which is wrong here, so those two cases are phrased for a file.
 *
 * @param rawError  The rejected error, before unwrapping.
 * @param subject   What was being fetched, e.g. "file" or "document".
 */
export async function describeDownloadError(rawError: unknown, subject = 'file'): Promise<string> {
  const error = await normalizeBlobError(rawError);

  if (isTimeoutError(error)) {
    return `The ${subject} took too long to prepare. Please try again.`;
  }
  if (isNetworkError(error)) {
    return `Could not reach the server to fetch the ${subject}. Check your connection and try again.`;
  }
  return getErrorMessage(error);
}
